import { navigation } from '@/navigation';

/** sessionStorage key: the URL a signed-out visit was sent away from, with the person it was for. */
export const RETURN_URL_KEY = 'auth_returnUrl';

/** sessionStorage key: the last person authenticated in this tab (User Node ID). */
export const LAST_USER_ID_KEY = 'auth_lastUserId';

/** What is stored: the URL and the person signed in on the tab when it was saved, if anyone. */
interface SavedReturnUrl {
  url: string;
  userId: string | null;
}

function isSavedReturnUrl(value: unknown): value is SavedReturnUrl {
  return (
    typeof value === 'object' &&
    value !== null &&
    'url' in value &&
    'userId' in value &&
    typeof value.url === 'string' &&
    (value.userId === null || typeof value.userId === 'string')
  );
}

/** True for a control character, which the URL parser strips (`/\t/host` reads as `//host`). */
function isControlCharacter(char: string): boolean {
  const code = char.charCodeAt(0);
  return code < 0x20 || code === 0x7f;
}

/** A path on this origin: one leading slash, no backslash and no control character. */
export function isSameOriginPath(url: string): boolean {
  return (
    url.startsWith('/') &&
    !url.startsWith('//') &&
    !url.includes('\\') &&
    ![...url].some(isControlCharacter)
  );
}

/** `redirectTo` when it is a path on this origin, else the dashboard. */
export function safeRedirect(redirectTo: string | null | undefined): string {
  return redirectTo && isSameOriginPath(redirectTo) ? redirectTo : navigation.dashboard();
}

/** Records who is signed in on this tab, so a URL saved at their forced sign-out stays theirs. */
export function rememberLastUser(userId: string): void {
  try {
    sessionStorage.setItem(LAST_USER_ID_KEY, userId);
  } catch {
    // Without storage a later return URL is simply unowned.
  }
}

/** Forgets who was signed in — called on a deliberate sign-out. */
export function forgetLastUser(): void {
  try {
    sessionStorage.removeItem(LAST_USER_ID_KEY);
  } catch {
    // Nothing to forget if storage is unavailable.
  }
}

/** Saves `url` to return to after sign-in, owned by the last person signed in on this tab. */
export function rememberReturnUrl(url: string): void {
  try {
    const saved: SavedReturnUrl = { url, userId: sessionStorage.getItem(LAST_USER_ID_KEY) };
    sessionStorage.setItem(RETURN_URL_KEY, JSON.stringify(saved));
  } catch {
    // Storage is a convenience here — a blocked one only costs the return.
  }
}

/**
 * Reads the saved URL and forgets it. Returned only to the person it was saved for, or to anyone
 * when nobody was signed in on the tab at the time; another person's URL is dropped.
 */
export function takeReturnUrl(userId: string): string | null {
  try {
    const raw = sessionStorage.getItem(RETURN_URL_KEY);
    forgetReturnUrl();
    if (raw === null) return null;
    const saved: unknown = JSON.parse(raw);
    if (!isSavedReturnUrl(saved) || !isSameOriginPath(saved.url)) return null;
    return saved.userId === null || saved.userId === userId ? saved.url : null;
  } catch {
    return null;
  }
}

/** Drops the saved return URL. */
export function forgetReturnUrl(): void {
  try {
    sessionStorage.removeItem(RETURN_URL_KEY);
  } catch {
    // Nothing to forget if storage is unavailable.
  }
}
